import blogTopicMapping from "../config/blogTopicMapping.json";

export const BLOG_DEFAULT_TOPIC = blogTopicMapping.defaultTopic;
export const BLOG_TOPIC_LABELS = blogTopicMapping.topics;
export const BLOG_TAG_TO_TOPIC = blogTopicMapping.tagToTopic;

const FALLBACK_TOPIC_MATCHERS = [
  {
    topic: "New hire onboarding",
    keywords: ["onboarding", "new hire", "new hires", "digital onboarding", "gen z", "micro-learning", "micro learning"],
  },
  {
    topic: "Allergen & dietary safety",
    keywords: ["allergy", "allergen", "ingredient", "dietary", "guest question", "guest questions", "specials", "menu updates", "kitchen"],
  },
  {
    topic: "Wine training",
    keywords: ["wine", "sommelier", "beverage", "glass pour"],
  },
  {
    topic: "Upselling & revenue",
    keywords: ["upsell", "revenue", "sales", "profit", "check average", "check averages"],
  },
  {
    topic: "Manager operations & retention",
    keywords: ["operations", "manager", "retention", "turnover", "staffing", "restaurant management", "burnout"],
  },
  {
    topic: "Service standards & hospitality",
    keywords: ["hospitality", "service", "guest", "foh", "front of house", "server confidence", "communication"],
  },
  {
    topic: "Menu training systems",
    keywords: ["menu", "training", "education", "language acquisition", "sla", "systems", "automation", "interactive", "gamified"],
  },
];

export function normalizeBlogTag(tag) {
  return String(tag || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function normalizeBlogTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((tag) => String(tag || "").trim()).filter(Boolean);
  }

  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

export function buildBlogTagCounts(posts) {
  return posts.reduce((counts, post) => {
    normalizeBlogTags(post.tags).forEach((tag) => {
      const key = normalizeBlogTag(tag);
      counts[key] = (counts[key] || 0) + 1;
    });

    return counts;
  }, {});
}

export function resolveTopicForTag(tag) {
  const normalizedTag = normalizeBlogTag(tag);

  if (BLOG_TAG_TO_TOPIC[normalizedTag]) {
    return BLOG_TAG_TO_TOPIC[normalizedTag];
  }

  const matchedFallback = FALLBACK_TOPIC_MATCHERS.find(({ keywords }) =>
    keywords.some((keyword) => normalizedTag.includes(keyword))
  );

  return matchedFallback?.topic || BLOG_DEFAULT_TOPIC;
}

export function resolvePrimaryTopic(post, tagCounts) {
  const tags = normalizeBlogTags(post.tags);
  if (!tags.length) return BLOG_DEFAULT_TOPIC;

  const topicScores = new Map();
  const firstTopicIndex = new Map();

  tags.forEach((tag, index) => {
    const normalizedTag = normalizeBlogTag(tag);
    const topic = resolveTopicForTag(normalizedTag);
    const weight = 1 / Math.max(1, tagCounts[normalizedTag] || 1);

    topicScores.set(topic, (topicScores.get(topic) || 0) + weight);
    if (!firstTopicIndex.has(topic)) {
      firstTopicIndex.set(topic, index);
    }
  });

  const rankedTopics = [...topicScores.entries()].sort((left, right) => {
    if (right[1] !== left[1]) {
      return right[1] - left[1];
    }

    const leftIndex = firstTopicIndex.get(left[0]) ?? Number.MAX_SAFE_INTEGER;
    const rightIndex = firstTopicIndex.get(right[0]) ?? Number.MAX_SAFE_INTEGER;

    if (leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }

    return BLOG_TOPIC_LABELS.indexOf(left[0]) - BLOG_TOPIC_LABELS.indexOf(right[0]);
  });

  return rankedTopics[0]?.[0] || BLOG_DEFAULT_TOPIC;
}